import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, CheckboxField, Icon, buttonClasses, cn } from '@forecastify/ui';
import {
  CONTROL_PANEL_PATHS,
  deleteTenantResponseSchema,
  operatorPlanListResponseSchema,
  operatorTenantSchema,
  type OperatorPlan,
  type DeleteTenantResponse,
  type OperatorTenant,
} from '@forecastify/shared';
import { useI18n, useT, type MessageKey, type Translate } from '../i18n';
import { useOperatorSession } from '../session';
import { AUDIT_PATH, TENANTS_PATH, tenantPath } from '../routes';
import { ActionDialog, DialogField } from '../chrome/action-dialog';
import { formatMoney, formatNumber } from '../chrome/format';
import { useCrumbs } from '../chrome/page-chrome';
import { FIELD, WARN_BUTTON } from '../chrome/tones';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C9 · The one irreversible act in this console, as its own address.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A SCREEN, NOT A DIALOG, AND THE REASON IS THAT THERE IS NO SUSPEND-FIRST GATE.** A live
 * paying customer can be removed in one sitting, so this confirmation is the only guard there is.
 * A dialog is dismissed by Escape, sized by what fits and read in a hurry; what stands between a
 * click and eighty-five tables of somebody's data needs room for the figures, four sentences that
 * have to be read, and a reason long enough to mean something in six months.
 *
 * ⚠️⚠️ **EVERY GUARD HERE IS A SECOND COPY OF A SERVER GUARD, NEVER THE ONLY ONE.** The slug is
 * compared in `OperatorTenantsService.deleteForGood` and the ten-character reason is
 * `deleteTenantRequestSchema`'s; a confirmation enforced only in the browser is a confirmation one
 * `fetch` skips. What the screen adds is that the refusal never arrives after a round trip — the
 * button's predicate is the server's predicate, so the operator is told what is missing while they
 * can still fix it.
 *
 * ⚠️⚠️ **THE MOCKUP'S THIRD PROOF — A FRESH TOTP CODE — IS NOT DRAWN, ON PURPOSE.** It guards the
 * right threat (a console left open on an unlocked laptop), and every way of checking it today is
 * worse than not checking it: `verifyTotpFactor` needs a GoTrue access token the operator session
 * deliberately does not hold, keeping one would widen what a stolen session token grants, and
 * re-running `signIn` would mint a second session and put a phantom device in the operator's own
 * list. The fix is a verify-only method that mints nothing, and it is its own piece of work. The
 * field is absent rather than present-and-ignored, because a security control that does nothing is
 * worse than a missing one.
 *
 * ⚠️ **"Suspend instead" suspends from HERE rather than sending the operator back for it.** An
 * escape hatch that is a link to the screen with the real control is an escape hatch nobody takes
 * at the moment it matters.
 */

type Load =
  | { readonly state: 'loading' }
  | { readonly state: 'failed'; readonly code: string }
  | { readonly state: 'ready'; readonly tenant: OperatorTenant };

/** What the receipt needs, captured BEFORE the delete — afterwards there is nothing to ask. */
interface Receipt extends DeleteTenantResponse {
  readonly tenantId: string;
  readonly workspaceCount: number;
  readonly memberCount: number;
  readonly reason: string;
}

export function TenantDeleteScreen(): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const session = useOperatorSession();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [load, setLoad] = useState<Load>({ state: 'loading' });
  /* The plan's currency, so the monthly value reads *574 EUR* rather than a bare number. */
  const [plans, setPlans] = useState<readonly OperatorPlan[]>([]);
  const [acks, setAcks] = useState<Readonly<Record<string, boolean>>>({});
  const [slug, setSlug] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);

  /* The escape hatch's own state, kept apart from the deletion's. */
  const [suspending, setSuspending] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  const read = useCallback(async (): Promise<void> => {
    if (id === undefined) return;
    const result = await session.request<unknown>(CONTROL_PANEL_PATHS.tenant(id));
    if (!result.ok) {
      if (result.status === 401) return;
      setLoad({ state: 'failed', code: result.code });
      return;
    }
    const parsed = operatorTenantSchema.safeParse(result.value);
    setLoad(
      parsed.success
        ? { state: 'ready', tenant: parsed.data }
        : { state: 'failed', code: 'unknown' },
    );
  }, [id, session]);

  useEffect(() => {
    void read();
  }, [read]);

  useEffect(() => {
    void session.request<unknown>(CONTROL_PANEL_PATHS.plans).then((result) => {
      if (!result.ok) return;
      const parsed = operatorPlanListResponseSchema.safeParse(result.value);
      if (parsed.success) setPlans(parsed.data.data);
    });
  }, [session]);

  /* ⚠️ `delete.error.slug` rather than the generic `validation_failed`: the server answers a
     mismatched slug with `validation_failed` on purpose (the operator is on the right screen and
     typed the wrong thing), and telling them which is the entire point of asking. */
  const messageFor = (code: string): string => {
    const known: Readonly<Record<string, MessageKey>> = {
      not_found: 'error.not_found',
      validation_failed: 'delete.error.slug',
      conflict: 'error.conflict',
      unauthenticated: 'error.unauthenticated',
      unreachable: 'error.unreachable',
    };
    const key = known[code];
    return key === undefined ? t('error.unknown') : t(key);
  };

  /* ⚠️ *Szervezetek › Hegyvidék Studio Kft. › Végleges törlés* — the mockup's three levels. The
     receipt's trail drops the middle one, because that organisation no longer exists. */
  useCrumbs(
    receipt !== null
      ? [{ label: t('nav.tenants'), href: TENANTS_PATH }, { label: t('delete.done.kicker') }]
      : load.state === 'ready'
        ? [
            { label: t('nav.tenants'), href: TENANTS_PATH },
            { label: load.tenant.name, href: tenantPath(load.tenant.id) },
            { label: t('tenant.tab.danger') },
          ]
        : [{ label: t('nav.tenants'), href: TENANTS_PATH }],
  );

  if (receipt !== null) return <Receipt_ receipt={receipt} />;

  if (load.state !== 'ready') {
    return (
      <div data-screen="tenant-delete" className="flex flex-col items-start gap-2 p-4">
        {load.state === 'loading' ? (
          <p role="status" className="text-muted-foreground text-[13px]">
            {t('tenants.loading')}
          </p>
        ) : (
          <p role="alert" className="text-[13px]">
            {load.code === 'not_found' ? t('tenant.notFound') : t('tenants.failed')}
          </p>
        )}
      </div>
    );
  }

  const tenant = load.tenant;
  const currency = plans.find((plan) => plan.id === tenant.planId)?.currency ?? null;
  /* ⚠️ *574 EUR*, not `574.00`: the amount is a string on the wire and gets its currency here. */
  const money = (amount: string): string =>
    currency === null ? amount : formatMoney(amount, currency, locale);
  const live = tenant.status !== 'suspended';
  const paying = tenant.monthlyValue !== null;

  /**
   * ⚠️⚠️ **THE ACKNOWLEDGEMENTS ARE DERIVED FROM THIS TENANT, NOT A FIXED LIST.** "2 workspaces
   * are removed" and "40 people lose access at once" are the sentences that make somebody stop;
   * a generic "this cannot be undone" is one everybody has clicked past. The paying row appears
   * only for a tenant that pays, because a row nobody can disagree with teaches people to tick
   * without reading.
   */
  const ackRows: readonly { readonly key: string; readonly text: string }[] = [
    {
      key: 'workspaces',
      text: t('delete.ack.workspaces', { count: tenant.workspaceCount }),
    },
    {
      key: 'people',
      text: live
        ? t('delete.ack.peopleLive', { count: tenant.memberCount })
        : t('delete.ack.people', { count: tenant.memberCount }),
    },
    ...(tenant.monthlyValue === null
      ? []
      : [
          {
            key: 'paying',
            text: t('delete.ack.paying', { amount: money(tenant.monthlyValue) }),
          },
        ]),
    { key: 'undo', text: t('delete.ack.undo') },
  ];

  const allAcked = ackRows.every((row) => acks[row.key] === true);
  const slugOk = slug === tenant.slug;
  const reasonOk = reason.trim().length >= 10;

  /* ⚠️ Three literal `t()` calls, not `t(keyFor(…))`: `i18n:check` reads literal arguments only,
     and a key reached through a variable reports as an orphan somebody then deletes. */
  const missing: readonly string[] = [
    allAcked ? null : t('delete.missing.acks'),
    slugOk ? null : t('delete.missing.slug'),
    reasonOk ? null : t('delete.missing.reason'),
  ].filter((entry): entry is string => entry !== null);

  const blocked = missing.length > 0;

  const remove = (): void => {
    if (id === undefined || busy || blocked) return;
    setBusy(true);
    setFailure(null);

    void session
      .request<unknown>(CONTROL_PANEL_PATHS.deleteTenant(id), {
        method: 'DELETE',
        body: JSON.stringify({ slug, reason: reason.trim() }),
      })
      .then((result) => {
        setBusy(false);
        if (!result.ok) {
          if (result.status === 401) return;
          setFailure(messageFor(result.code));
          return;
        }
        const parsed = deleteTenantResponseSchema.safeParse(result.value);
        if (!parsed.success) {
          setFailure(t('error.unknown'));
          return;
        }
        /* ⚠️ The counts come from the tenant we were looking at, because the row they describe no
           longer exists — there is nothing left to ask for them. */
        setReceipt({
          ...parsed.data,
          tenantId: id,
          workspaceCount: tenant.workspaceCount,
          memberCount: tenant.memberCount,
          reason: reason.trim(),
        });
      });
  };

  const suspend = (): void => {
    if (id === undefined || busy || suspendReason.trim().length < 10) return;
    setBusy(true);
    setFailure(null);

    void session
      .request<unknown>(CONTROL_PANEL_PATHS.suspend(id), {
        method: 'POST',
        body: JSON.stringify({ reason: suspendReason.trim() }),
      })
      .then((result) => {
        setBusy(false);
        if (!result.ok) {
          if (result.status === 401) return;
          setFailure(messageFor(result.code));
          return;
        }
        setSuspending(false);
        setSuspendReason('');
        /* ⚠️ Back to the organisation, not back to this screen. Somebody who took the escape hatch
           has decided not to delete, and leaving them in front of the delete button undoes that. */
        void navigate(tenantPath(id));
      });
  };

  return (
    <div data-screen="tenant-delete" className="flex flex-col">
      {/* ⚠️ A destructive hairline across the whole width, before anything else. It is the one
          thing that reads at a glance as "this page is not like the others". */}
      <div className="border-destructive h-0 border-t-2" />

      <div className="mx-auto grid w-full max-w-[1040px] gap-8 p-6 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="flex min-w-0 flex-col gap-6">
          <div className="flex flex-col gap-1.5">
            <span className="text-destructive text-[11px] font-medium tracking-[.03em] uppercase">
              {t('danger.tier')}
            </span>
            <h1 className="text-[22px] leading-[28px] font-semibold tracking-[-.015em] text-pretty">
              {t('delete.title', { name: tenant.name })}
            </h1>
            <p className="text-muted-foreground text-[13px]">{t('delete.lede')}</p>
          </div>

          {/* ── 1 · what it removes ──────────────────────────────────────────────────────── */}
          <section aria-label={t('delete.step1')} className="flex flex-col gap-3">
            <Step number={1} label={t('delete.step1')} />

            {live ? (
              <div className="border-destructive/50 bg-destructive/[0.06] flex flex-wrap items-center gap-3 rounded-lg border p-3.5">
                <Icon name="triangle-alert" size={16} className="text-destructive shrink-0" />
                <span className="text-destructive min-w-0 flex-1 text-[13px] font-medium text-pretty">
                  {paying && tenant.monthlyValue !== null
                    ? t('delete.livePaying', { amount: money(tenant.monthlyValue) })
                    : t('delete.live')}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn('h-7 px-2.5 text-[13px]', WARN_BUTTON)}
                  disabled={busy}
                  onClick={() => {
                    setSuspendReason('');
                    setSuspending(true);
                  }}
                >
                  {t('delete.suspendInstead')}
                </Button>
              </div>
            ) : null}

            <dl className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] overflow-hidden rounded-lg border">
              <Fact
                label={t('danger.status')}
                value={t(STATUS_LABEL[tenant.status])}
                alarm={live}
              />
              <Fact
                label={t('danger.monthly')}
                value={
                  tenant.monthlyValue === null || currency === null
                    ? '–'
                    : formatMoney(tenant.monthlyValue, currency, locale)
                }
                alarm={paying}
              />
              <Fact
                label={t('tenant.workspaces')}
                value={formatNumber(tenant.workspaceCount, locale)}
              />
              <Fact label={t('tenant.members')} value={formatNumber(tenant.memberCount, locale)} />
              <Fact
                label={t('tenant.seats')}
                value={`${String(tenant.seatsUsed)} / ${
                  tenant.seatLimit === null ? '∞' : String(tenant.seatLimit)
                }`}
              />
              <Fact label={t('tenant.lastRequest')} value={ago(tenant.lastRequestAt, t)} />
            </dl>
          </section>

          {/* ── 2 · acknowledge ──────────────────────────────────────────────────────────── */}
          <section aria-label={t('delete.step2')} className="flex flex-col gap-3">
            <Step number={2} label={t('delete.step2')} />
            <div className="flex flex-col rounded-lg border">
              {ackRows.map((row) => (
                <CheckboxField
                  key={row.key}
                  align="start"
                  label={row.text}
                  checked={acks[row.key] === true}
                  onCheckedChange={(next) => {
                    setAcks((current) => ({ ...current, [row.key]: next }));
                  }}
                  className="hover:bg-muted/40 border-b px-3.5 py-3 transition-colors last:border-0"
                />
              ))}
            </div>
          </section>

          {/* ── 3 · confirm ──────────────────────────────────────────────────────────────── */}
          <section aria-label={t('delete.step3')} className="flex flex-col gap-3">
            <Step number={3} label={t('delete.step3')} />
            <div className="flex flex-col gap-4 rounded-lg border p-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="delete-slug" className="text-[12.5px] font-medium">
                  {t('delete.slugLabel', { slug: tenant.slug })}
                </label>
                <input
                  id="delete-slug"
                  className={cn(FIELD, 'font-mono')}
                  value={slug}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => {
                    setSlug(event.target.value);
                  }}
                  /* ⚠️⚠️ **PASTING IS REFUSED, AND IT IS THE WHOLE POINT OF ASKING.** The slug is
                     printed one line above, so a paste proves nothing about whether anybody read
                     it. This is a nudge, not a control — the server compares the string either
                     way — and it is the cheapest of the three proofs for exactly that reason. */
                  onPaste={(event) => {
                    event.preventDefault();
                  }}
                />
                <span className="text-muted-foreground text-[11.5px]">{t('delete.slugHint')}</span>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="delete-reason" className="text-[12.5px] font-medium">
                  {t('delete.reason')}
                </label>
                <textarea
                  id="delete-reason"
                  className="bg-muted/50 hover:border-input focus:border-ring focus:ring-ring/30 min-h-[72px] rounded-md border border-transparent px-2.5 py-2 text-[13px] outline-none focus:ring-[3px]"
                  value={reason}
                  placeholder={t('delete.reasonHint')}
                  onChange={(event) => {
                    setReason(event.target.value);
                  }}
                />
              </div>
            </div>
          </section>

          {failure === null ? null : (
            <p role="alert" className="text-destructive text-[12.5px]">
              {failure}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 border-t pt-4">
            <Button
              variant="destructive"
              className="h-9 disabled:opacity-45"
              disabled={busy || blocked}
              onClick={() => {
                remove();
              }}
            >
              <Icon name="trash" size={14} />
              {busy ? t('delete.working') : t('delete.confirm')}
            </Button>
            <Link
              to={tenantPath(tenant.id)}
              className={buttonClasses('ghost', 'default', 'text-foreground h-9')}
            >
              {t('delete.cancel')}
            </Link>
            {/* ⚠️⚠️ **WHAT IS MISSING IS NAMED, NOT JUST WITHHELD.** A disabled destructive button
                with no explanation is the control people click repeatedly and then report as
                broken — and here the four things it waits on are spread down a long page where
                the unfinished one is off screen. */}
            <span
              role="status"
              className={`min-w-0 flex-1 text-[12px] ${blocked ? 'text-muted-foreground' : 'text-[var(--status-ok-text)]'}`}
            >
              {blocked ? `${t('delete.missing')} ${missing.join(', ')}` : t('delete.ready')}
            </span>
          </div>
        </div>

        <aside className="flex flex-col gap-2 self-start rounded-lg border p-4 lg:sticky lg:top-4">
          <h2 className="text-[13px] font-semibold">{t('delete.why.title')}</h2>
          <p className="text-muted-foreground text-[12.5px] leading-[1.55] text-pretty">
            {t('delete.why.body')}
          </p>
        </aside>
      </div>

      <ActionDialog
        open={suspending}
        onClose={() => {
          setSuspending(false);
        }}
        title={t('tenant.suspend.title', { name: tenant.name })}
        description={t('tenant.suspend.lede', {
          people: formatNumber(tenant.memberCount, locale),
          workspaces: formatNumber(tenant.workspaceCount, locale),
        })}
        tier={{ label: t('tenant.suspend.tier'), tone: 'warn' }}
        cancelLabel={t('tenant.suspend.cancel')}
        blockedText={
          suspendReason.trim().length < 10
            ? t('tenant.suspend.short', { count: 10 - suspendReason.trim().length })
            : ''
        }
        confirm={{
          label: busy ? t('tenant.suspend.working') : t('tenant.suspend.confirm'),
          tone: 'warn',
          disabled: busy || suspendReason.trim().length < 10,
          onClick: suspend,
        }}
      >
        <DialogField label={t('tenant.suspend.reason')} htmlFor="escape-suspend-reason">
          <textarea
            id="escape-suspend-reason"
            className="bg-muted/50 hover:border-input focus:border-ring focus:ring-ring/30 min-h-[80px] rounded-md border border-transparent px-2.5 py-2 text-[13px] outline-none focus:ring-[3px]"
            value={suspendReason}
            placeholder={t('tenant.suspend.reasonHint')}
            onChange={(event) => {
              setSuspendReason(event.target.value);
            }}
          />
        </DialogField>
      </ActionDialog>
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The receipt. The organisation is gone; this is what is left of it.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **RENDERED IN PLACE RATHER THAN AT ITS OWN ADDRESS, AND THE REASON IS THAT IT CANNOT BE
 * RELOADED.** Every other screen in this console answers a `GET`; this one describes a row that no
 * longer exists, so an address for it would be an address that works once and 404s on refresh.
 * Everything it shows came back from the `DELETE` or was held on screen when it was sent.
 *
 * ⚠️ **The audit id is the only durable thing here**, and it is a link rather than a number to copy
 * out: the audit log outlives the organisation (`operator_audit_log` references `superadmins` and
 * nothing else), so the entry is still there to open.
 */
function Receipt_({ receipt }: { readonly receipt: Receipt }): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const session = useOperatorSession();

  const rows: readonly { readonly label: string; readonly value: string; readonly mono?: true }[] =
    [
      {
        label: t('delete.done.removed'),
        value: t('delete.done.removedValue', {
          workspaces: receipt.workspaceCount,
          people: receipt.memberCount,
        }),
      },
      {
        label: t('delete.done.by'),
        value: session.operator?.name ?? session.operator?.email ?? '—',
      },
      {
        label: t('delete.done.at'),
        value: new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
          new Date(receipt.deletedAt),
        ),
      },
      { label: t('delete.done.reason'), value: receipt.reason },
      { label: t('delete.done.audit'), value: receipt.auditId, mono: true },
    ];

  return (
    <div
      data-screen="tenant-delete"
      className="mx-auto flex w-full max-w-[560px] flex-col gap-5 px-6 py-12"
    >
      <div className="flex flex-col gap-1.5">
        <span className="text-muted-foreground text-[11px] font-medium tracking-[.03em] uppercase">
          {t('delete.done.kicker')}
        </span>
        <h1 className="text-[22px] leading-[28px] font-semibold tracking-[-.015em]">
          {t('delete.done.title', { name: receipt.name })}
        </h1>
        <p className="text-muted-foreground text-[13px] leading-[1.55] text-pretty">
          {t('delete.done.body')}
        </p>
      </div>

      <dl className="flex flex-col rounded-lg border">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-baseline gap-3 border-b px-3.5 py-2.5 last:border-0"
          >
            <dt className="text-muted-foreground w-[130px] shrink-0 text-[12.5px]">{row.label}</dt>
            <dd
              className={`min-w-0 flex-1 text-[13px] tabular-nums ${row.mono === true ? 'text-[12px] break-all' : ''}`}
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>

      <div className="flex flex-wrap gap-2">
        {/* ⚠️ Both navigate, so both are anchors wearing `buttonClasses` rather than buttons with
            an onClick — a `<button>` that changes the address loses the middle click, and a
            reachability check cannot see it at all. */}
        <Link to={TENANTS_PATH} className={buttonClasses('default', 'sm')}>
          {t('delete.done.toTenants')}
        </Link>
        <Link
          to={`${AUDIT_PATH}?resourceType=tenant&resourceId=${encodeURIComponent(receipt.tenantId)}`}
          className={buttonClasses('outline', 'sm')}
        >
          {t('delete.done.openAudit')}
        </Link>
      </div>
    </div>
  );
}

function Step({ number, label }: { readonly number: number; readonly label: string }): ReactNode {
  return (
    <h2 className="flex items-center gap-2 text-[13px] font-semibold">
      {/* ⚠️ Numbered because this genuinely IS a sequence: the figures decide whether to go on, the
          acknowledgements are read with them in view, and the typing comes last. */}
      <span
        aria-hidden
        className="bg-foreground text-background grid size-5 place-items-center rounded-full text-[11px]"
      >
        {number}
      </span>
      {label}
    </h2>
  );
}

function Fact({
  label,
  value,
  alarm = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly alarm?: boolean;
}): ReactNode {
  return (
    <div className="flex min-w-0 flex-col gap-1 border-r border-b p-3 last:border-r-0">
      <dt className="text-muted-foreground truncate text-[12px]">{label}</dt>
      <dd
        className={`text-[18px] leading-[1.25] font-semibold tracking-[-.01em] tabular-nums ${alarm ? 'text-destructive' : ''}`}
      >
        {value}
      </dd>
    </div>
  );
}

/** ⚠️ The same arithmetic the Overview tab does, because it is the same question: still here? */
function ago(at: string | null, t: Translate): string {
  if (at === null) return t('tenant.lastRequest.never');
  const minutes = Math.max(0, Math.round((Date.now() - new Date(at).getTime()) / 60_000));
  if (minutes < 60) return t('tenant.ago.minutes', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 48) return t('tenant.ago.hours', { count: hours });
  return t('tenant.ago.days', { count: Math.round(hours / 24) });
}

const STATUS_LABEL = {
  trial: 'status.trial',
  active: 'status.active',
  suspended: 'status.suspended',
  awaiting_signup: 'status.awaitingSignup',
} as const satisfies Readonly<Record<OperatorTenant['status'], MessageKey>>;
